import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const TrophyIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-8 w-8"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9H4a2 2 0 0 1-2-2V5h4" />
    <path d="M18 9h2a2 2 0 0 0 2-2V5h-4" />
    <path d="M6 2h12v7a6 6 0 0 1-12 0V2Z" />
    <path d="M12 15v4" />
    <path d="M8 19h8" />
  </svg>
);

/**
 * Full-screen overlay shown to anonymous users after quiz completion.
 * Displays their today's rank, score, and percentile with a sign-up CTA.
 *
 * Props:
 *   result — the result object from QuizPlayer (stats, rank, percentile, rankIsToday)
 *   onDismiss — called when the user closes without registering
 */
const GuestRankOverlay = ({ result, onDismiss }) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  // Animate in after mount
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  if (!result) return null;

  const { stats, rank, percentile, tiedWith, rankIsToday } = result;

  const handleSave = () => {
    navigate("/register");
  };

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(onDismiss, 300);
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center px-4 transition-opacity duration-300 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
      style={{ background: "rgba(5, 8, 28, 0.85)", backdropFilter: "blur(6px)" }}
      onClick={handleDismiss}
    >
      {/* Card */}
      <div
        className={`relative w-full max-w-sm transition-all duration-300 ${
          visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
        }`}
        style={{
          background: "linear-gradient(160deg, #0d1640 0%, #1a1060 60%, #0d1640 100%)",
          border: "1px solid rgba(201, 162, 74, 0.5)",
          borderRadius: "20px",
          boxShadow: "0 0 40px rgba(201, 162, 74, 0.15), 0 20px 60px rgba(0,0,0,0.6)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Dismiss button */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Close"
          className="absolute right-4 top-4 text-white/30 transition hover:text-white/70"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Trophy icon circle */}
        <div className="flex justify-center pt-8">
          <div
            className="flex h-16 w-16 items-center justify-center rounded-full"
            style={{
              background: "linear-gradient(135deg, rgba(255,215,0,0.15), rgba(201,162,74,0.08))",
              border: "1px solid rgba(201, 162, 74, 0.5)",
            }}
          >
            <span style={{ color: "#C9A24A" }}>
              <TrophyIcon />
            </span>
          </div>
        </div>

        <div className="px-8 pb-8 pt-5 text-center">
          {/* Score */}
          <p className="text-sm font-medium text-white/60">
            You scored {stats.score}/{stats.total}
          </p>

          {/* Rank — large gold gradient */}
          <p
            className="mt-3 leading-none"
            style={{
              fontSize: "48px",
              fontWeight: 800,
              background: "linear-gradient(180deg, #FFD700, #C9A24A)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              textShadow: "0px 4px 12px rgba(255, 215, 0, 0.4)",
            }}
          >
            #{rank ?? "–"}{rankIsToday ? " TODAY" : ""}
          </p>

          {/* Percentile / tied-with */}
          {tiedWith != null ? (
            <p className="mt-3 text-sm font-medium text-white/70">
              Tied with <span className="font-semibold text-white">{tiedWith}</span> other players
            </p>
          ) : (
            <p className="mt-3 text-sm font-medium text-white/70">
              You beat <span className="font-semibold text-white">{percentile}%</span> of players
            </p>
          )}

          {/* Divider */}
          <div
            className="mx-auto my-6 h-px w-3/4"
            style={{ background: "linear-gradient(90deg, transparent, rgba(201,162,74,0.3), transparent)" }}
          />

          {/* CTA text */}
          <p className="text-sm font-medium text-white/60">
            Save your position before it&apos;s gone
          </p>

          {/* CTA button */}
          <button
            type="button"
            onClick={handleSave}
            className="mt-4 w-full rounded-full py-3 text-sm font-bold tracking-wide transition active:scale-95"
            style={{
              background: "linear-gradient(180deg, #FFD700, #C9A24A)",
              color: "#1a0e00",
              boxShadow: "0 4px 20px rgba(255, 215, 0, 0.3)",
            }}
          >
            Save My Rank
          </button>

          {/* Skip link */}
          <button
            type="button"
            onClick={handleDismiss}
            className="mt-3 block w-full text-xs text-white/30 transition hover:text-white/50"
          >
            No thanks, continue without saving
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestRankOverlay;
